import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { 
  AnalyticsConfiguration,
  AnalyticsCredentialStatus,
  DiscoverSchemaResponse,
  AnalyticsSchemaTable,
  ProviderConfigurationStatus,
  AnalyticsMessageRequest,
  ConversationResponse,
  CreateAnalyticsConfigurationRequest
} from '../types';
import { api } from '../services/api';
import { useAuth } from './AuthContext';

// Analytics State Interface
interface AnalyticsState {
  // Configuration
  configuration: AnalyticsConfiguration | null;
  configLoading: boolean;
  configError: string | null;
  
  // Provider Status
  providers: {
    zoho: ProviderState;
    powerbi: ProviderState;
  };
  
  // Schema
  schema: {
    zoho: SchemaCache | null;
    powerbi: SchemaCache | null;
  };
  schemaLoading: boolean;
  schemaError: string | null;
  
  // Analytics Conversations
  conversations: AnalyticsConversation[];
  activeConversationId: string | null;
  isAnalyticsChatEnabled: boolean;
  
  // UI State
  activeProvider: 'zoho' | 'powerbi' | null;
  setupStep: 'configuration' | 'oauth' | 'schema' | 'complete';
  setupModalOpen: boolean;
}

interface ProviderState {
  configured: boolean;
  connected: boolean;
  authorized: boolean;
  credentialStatus: AnalyticsCredentialStatus | null;
  loading: boolean;
  error: string | null;
}

interface SchemaCache {
  data: DiscoverSchemaResponse | null;
  lastUpdated: string | null;
  loading: boolean;
  error: string | null;
}

interface AnalyticsConversation {
  id: string;
  title: string;
  provider: 'zoho' | 'powerbi';
  schemaContext: string[];
  messages: AnalyticsMessage[];
  createdAt: string;
  lastMessageAt: string;
}

interface AnalyticsMessage {
  id: string;
  content: string;
  sender: 'user' | 'assistant';
  timestamp: string;
  analyticsContext?: {
    tables: string[];
    columns: string[];
    queryType: 'aggregation' | 'filter' | 'visualization' | 'insight';
    suggestedVisualization?: 'chart' | 'table' | 'metric';
  };
  visualization?: any;
  chart?: {
    base64: string;
    format: string;
  };
  rows?: any[];
  columns?: any[];
  rowCount?: number;
  sql?: string;
}

// Action Types
type AnalyticsAction =
  | { type: 'SET_LOADING'; payload: { section: keyof AnalyticsState; loading: boolean } }
  | { type: 'SET_ERROR'; payload: { section: string; error: string | null } }
  | { type: 'SET_CONFIGURATION'; payload: AnalyticsConfiguration | null }
  | { type: 'SET_PROVIDER_STATUS'; payload: { provider: 'zoho' | 'powerbi'; status: Partial<ProviderState> } }
  | { type: 'SET_CREDENTIAL_STATUS'; payload: { provider: 'zoho' | 'powerbi'; status: AnalyticsCredentialStatus } }
  | { type: 'SET_SCHEMA'; payload: { provider: 'zoho' | 'powerbi'; schema: DiscoverSchemaResponse | null } }
  | { type: 'SET_SCHEMA_LOADING'; payload: boolean }
  | { type: 'SET_SCHEMA_ERROR'; payload: string | null }
  | { type: 'SET_ACTIVE_PROVIDER'; payload: 'zoho' | 'powerbi' | null }
  | { type: 'SET_SETUP_STEP'; payload: 'configuration' | 'oauth' | 'schema' | 'complete' }
  | { type: 'SET_SETUP_MODAL'; payload: boolean }
  | { type: 'ADD_CONVERSATION'; payload: AnalyticsConversation }
  | { type: 'SET_ACTIVE_CONVERSATION'; payload: string | null }
  | { type: 'ADD_MESSAGE'; payload: { conversationId: string; message: AnalyticsMessage } }
  | { type: 'UPDATE_CONVERSATION'; payload: { id: string; updates: Partial<AnalyticsConversation> } }
  | { type: 'ENABLE_ANALYTICS_CHAT'; payload: boolean };

// Initial State
const initialState: AnalyticsState = {
  configuration: null,
  configLoading: false,
  configError: null,
  
  providers: {
    zoho: {
      configured: false,
      connected: false,
      authorized: false,
      credentialStatus: null,
      loading: false,
      error: null,
    },
    powerbi: {
      configured: false,
      connected: false,
      authorized: false,
      credentialStatus: null,
      loading: false,
      error: null,
    },
  },
  
  schema: {
    zoho: null,
    powerbi: null,
  },
  schemaLoading: false,
  schemaError: null,
  
  conversations: [],
  activeConversationId: null,
  isAnalyticsChatEnabled: false,
  
  activeProvider: null,
  setupStep: 'configuration',
  setupModalOpen: false,
};

// Reducer
function analyticsReducer(state: AnalyticsState, action: AnalyticsAction): AnalyticsState {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        [`${action.payload.section}Loading`]: action.payload.loading,
      } as AnalyticsState;
      
    case 'SET_ERROR':
      return {
        ...state,
        [`${action.payload.section}Error`]: action.payload.error,
      } as AnalyticsState;
      
    case 'SET_CONFIGURATION':
      return {
        ...state,
        configuration: action.payload,
        configError: null,
      };
      
    case 'SET_PROVIDER_STATUS':
      return {
        ...state,
        providers: {
          ...state.providers,
          [action.payload.provider]: {
            ...state.providers[action.payload.provider],
            ...action.payload.status,
          },
        },
      };
      
    case 'SET_CREDENTIAL_STATUS':
      return {
        ...state,
        providers: {
          ...state.providers,
          [action.payload.provider]: {
            ...state.providers[action.payload.provider],
            credentialStatus: action.payload.status,
            authorized: action.payload.status.authorized,
            connected: action.payload.status.connected,
            error: action.payload.status.error || null,
          },
        },
      };
      
    case 'SET_SCHEMA':
      return {
        ...state,
        schema: {
          ...state.schema,
          [action.payload.provider]: {
            data: action.payload.schema,
            lastUpdated: action.payload.schema ? new Date().toISOString() : null,
            loading: false,
            error: null,
          },
        },
      };
      
    case 'SET_SCHEMA_LOADING':
      return {
        ...state,
        schemaLoading: action.payload,
      };
      
    case 'SET_SCHEMA_ERROR':
      return {
        ...state,
        schemaError: action.payload,
        schemaLoading: false,
      };
      
    case 'SET_ACTIVE_PROVIDER':
      return {
        ...state,
        activeProvider: action.payload,
      };
      
    case 'SET_SETUP_STEP':
      return {
        ...state,
        setupStep: action.payload,
      };
      
    case 'SET_SETUP_MODAL':
      return {
        ...state,
        setupModalOpen: action.payload,
      };
      
    case 'ADD_CONVERSATION':
      return {
        ...state,
        conversations: [...state.conversations, action.payload],
      };
      
    case 'SET_ACTIVE_CONVERSATION':
      return {
        ...state,
        activeConversationId: action.payload,
      };
      
    case 'ADD_MESSAGE':
      const updatedConversations = state.conversations.map(conv =>
        conv.id === action.payload.conversationId
          ? {
              ...conv,
              messages: [...conv.messages, action.payload.message],
              lastMessageAt: action.payload.message.timestamp,
            }
          : conv
      );
      return {
        ...state,
        conversations: updatedConversations,
      };
      
    case 'UPDATE_CONVERSATION':
      return {
        ...state,
        conversations: state.conversations.map(conv =>
          conv.id === action.payload.id
            ? { ...conv, ...action.payload.updates }
            : conv
        ),
      };
      
    case 'ENABLE_ANALYTICS_CHAT':
      return {
        ...state,
        isAnalyticsChatEnabled: action.payload,
      };
      
    default:
      return state;
  }
}

// Context
const AnalyticsContext = createContext<{
  state: AnalyticsState;
  actions: AnalyticsActions;
} | null>(null);

// Actions Interface
interface AnalyticsActions {
  // Configuration Management
  loadConfiguration: () => Promise<void>;
  updateConfiguration: (config: Partial<CreateAnalyticsConfigurationRequest>) => Promise<void>;
  
  // Provider Management
  checkProviderStatus: (provider: 'zoho' | 'powerbi') => Promise<void>;
  connectProvider: (provider: 'zoho' | 'powerbi', config: any) => Promise<void>;
  refreshCredentials: (provider: 'zoho' | 'powerbi') => Promise<void>;
  revokeCredentials: (provider: 'zoho' | 'powerbi') => Promise<void>;
  
  // Schema Management
  discoverSchema: (provider: 'zoho' | 'powerbi', options?: { maxTables?: number; includeMetadata?: boolean }) => Promise<void>;
  getCachedSchema: (provider: 'zoho' | 'powerbi', forceRefresh?: boolean) => Promise<void>;
  clearSchemaCache: (provider?: 'zoho' | 'powerbi') => Promise<void>;
  
  // Conversation Management
  startAnalyticsConversation: (provider: 'zoho' | 'powerbi') => string;
  sendAnalyticsMessage: (conversationId: string, message: string, context?: any) => Promise<void>;
  setActiveConversation: (conversationId: string | null) => void;
  
  // UI Actions
  setActiveProvider: (provider: 'zoho' | 'powerbi' | null) => void;
  setSetupStep: (step: 'configuration' | 'oauth' | 'schema' | 'complete') => void;
  openSetupModal: () => void;
  closeSetupModal: () => void;
}

// Context Provider Component
export const AnalyticsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(analyticsReducer, initialState);
  const { user } = useAuth();

  // Load initial configuration
  useEffect(() => {
    loadConfiguration();
  }, []);

  // Automatically enable Analytics Chat when a provider is authorized and schema is loaded
  useEffect(() => {
    const anyAuthorized = Object.values(state.providers).some((p: ProviderState) => p.authorized);
    const anySchemaLoaded = Object.values(state.schema).some((s: SchemaCache | null) => !!s?.data);
    const shouldEnable = anyAuthorized && anySchemaLoaded;

    if (state.isAnalyticsChatEnabled !== shouldEnable) {
      dispatch({ type: 'ENABLE_ANALYTICS_CHAT', payload: shouldEnable });
    }
  }, [state.providers, state.schema, state.isAnalyticsChatEnabled]);

  // Actions Implementation
  const loadConfiguration = async () => {
    dispatch({ type: 'SET_LOADING', payload: { section: 'config' as keyof AnalyticsState, loading: true } });
    try {
      const config = await api.getAnalyticsConfiguration();
      dispatch({ type: 'SET_CONFIGURATION', payload: config });
      
      // Update provider status based on configuration
      if (config.zoho) {
        dispatch({ 
          type: 'SET_PROVIDER_STATUS', 
          payload: { 
            provider: 'zoho', 
            status: { configured: config.zoho.enabled } 
          } 
        });
      }
      if (config.powerbi) {
        dispatch({ 
          type: 'SET_PROVIDER_STATUS', 
          payload: { 
            provider: 'powerbi', 
            status: { configured: config.powerbi.enabled } 
          } 
        });
      }
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: { section: 'config', error: error.message } });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { section: 'config' as keyof AnalyticsState, loading: false } });
    }
  };

  const updateConfiguration = async (config: Partial<CreateAnalyticsConfigurationRequest>) => {
    dispatch({ type: 'SET_LOADING', payload: { section: 'config' as keyof AnalyticsState, loading: true } });
    try {
      // If no configuration exists (fresh start), create; otherwise, update
      const hasExisting = !!state.configuration && (state.configuration as any).tenantId;
      const updatedConfig = hasExisting
        ? await api.updateAnalyticsConfiguration(config)
        : await api.createAnalyticsConfiguration(config as CreateAnalyticsConfigurationRequest);

      dispatch({ type: 'SET_CONFIGURATION', payload: updatedConfig });
      
      // Update provider status based on configuration
      if (config.zoho) {
        dispatch({ 
          type: 'SET_PROVIDER_STATUS', 
          payload: { 
            provider: 'zoho', 
            status: { configured: config.zoho.enabled } 
          } 
        });
      }
      if (config.powerbi) {
        dispatch({ 
          type: 'SET_PROVIDER_STATUS', 
          payload: { 
            provider: 'powerbi', 
            status: { configured: config.powerbi.enabled } 
          } 
        });
      }
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: { section: 'config', error: error.message } });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { section: 'config' as keyof AnalyticsState, loading: false } });
    }
  };

  const connectProvider = async (provider: 'zoho' | 'powerbi', config: any) => {
    dispatch({ type: 'SET_PROVIDER_STATUS', payload: { provider, status: { loading: true } } });
    try {
      const tenantId = user?.tenantId || 'default';
      if (!user?.tenantId) {
        console.warn('User tenantId not available, falling back to "default" for OAuth flow');
      }
      
      let response;
      if (provider === 'zoho') {
        response = await api.connectZohoAnalytics({
          tenantId,
          region: config.defaultRegion || 'com',
          scope: 'ZohoAnalytics.data.read,ZohoAnalytics.metadata.read,ZohoAnalytics.modeling.read,ZohoAnalytics.export',
          successRedirectUrl: `${window.location.origin}/analytics?success=true`,
          failureRedirectUrl: `${window.location.origin}/analytics?error=true`,
          orgIdOrEmail: config.orgIdOrEmail,
          workspace: config.workspace,
          defaultTable: config.defaultTable,
          defaultSearchColumn: config.defaultSearchColumn,
        });
      } else {
        response = await api.connectPowerBIAnalytics({
          tenantId,
          aadTenantId: config.aadTenantId,
          scope: 'https://analysis.windows.net/powerbi/api/.default',
          successRedirectUrl: `${window.location.origin}/analytics?success=true`,
          failureRedirectUrl: `${window.location.origin}/analytics?error=true`,
          groupId: config.groupId,
          datasetId: config.datasetId,
          defaultTable: config.defaultTable,
          defaultSearchColumn: config.defaultSearchColumn,
        });
      }
      
      // Redirect to OAuth URL
      window.location.href = response.url;
    } catch (error: any) {
      dispatch({ type: 'SET_PROVIDER_STATUS', payload: { provider, status: { loading: false, error: error.message } } });
      throw error;
    }
  };

  const checkProviderStatus = async (provider: 'zoho' | 'powerbi') => {
    dispatch({ type: 'SET_PROVIDER_STATUS', payload: { provider, status: { loading: true } } });
    try {
      const status = await api.getAnalyticsCredentialStatus(provider);
      dispatch({ type: 'SET_CREDENTIAL_STATUS', payload: { provider, status } });
    } catch (error: any) {
      console.error('  ❌ Failed to check provider status:', error.message);
      dispatch({ type: 'SET_PROVIDER_STATUS', payload: { provider, status: { loading: false, error: error.message } } });
    }
  };

  const refreshCredentials = async (provider: 'zoho' | 'powerbi') => {
    dispatch({ type: 'SET_PROVIDER_STATUS', payload: { provider, status: { loading: true } } });
    try {
      const response = await api.refreshAnalyticsCredentials(provider);
      dispatch({ type: 'SET_CREDENTIAL_STATUS', payload: { provider, status: response.status } });
    } catch (error: any) {
      dispatch({ type: 'SET_PROVIDER_STATUS', payload: { provider, status: { loading: false, error: error.message } } });
      throw error;
    }
  };

  const revokeCredentials = async (provider: 'zoho' | 'powerbi') => {
    dispatch({ type: 'SET_PROVIDER_STATUS', payload: { provider, status: { loading: true } } });
    try {
      // 1) Revoke stored OAuth credentials (DELETE /api/analytics/credentials/{provider}/refresh)
      await api.revokeAnalyticsCredentials(provider);

      // 2) Clear provider schema cache
      try {
        await api.clearProviderCache(provider);
      } catch (e) {
        // Non-fatal: log to console for dev visibility, continue
        console.warn('Failed to clear schema cache after revocation', e);
      }

      // 3) Disable provider and purge config secrets. If no other provider is enabled, fully disable analytics configuration.
      const otherProvider = provider === 'zoho' ? 'powerbi' : 'zoho';
      const otherEnabled = !!state.configuration?.[otherProvider]?.enabled;

      if (!otherEnabled) {
        // No other provider enabled — fully disable analytics configuration (fresh start scenario)
        try {
          await api.deleteAnalyticsConfiguration();
        } catch (e) {
          console.warn('Failed to delete analytics configuration; falling back to disabling provider only', e);
          await api.disableProviderConfiguration(provider);
        }
      } else {
        // Keep the other provider as default if applicable
        const fallbackDefault = otherProvider as 'zoho' | 'powerbi';
        await api.disableProviderConfiguration(provider, fallbackDefault);
      }

      // 4) Update local state to reflect a clean slate for the provider
      dispatch({ type: 'SET_PROVIDER_STATUS', payload: { 
        provider, 
        status: { 
          configured: false, 
          connected: false, 
          authorized: false, 
          credentialStatus: null, 
          loading: false, 
          error: null 
        } 
      } });

      // Also clear schema from local state
      dispatch({ type: 'SET_SCHEMA', payload: { provider, schema: null } });

      // If we deleted the entire analytics configuration, refresh it in memory
      if (!otherEnabled) {
        try {
          await loadConfiguration();
        } catch {}
      }
    } catch (error: any) {
      dispatch({ type: 'SET_PROVIDER_STATUS', payload: { provider, status: { loading: false, error: error.message } } });
      throw error;
    }
  };

  // Schema Management
  const discoverSchema = async (provider: 'zoho' | 'powerbi', options?: { maxTables?: number; includeMetadata?: boolean }) => {
    dispatch({ type: 'SET_SCHEMA_LOADING', payload: true });
    try {
      const response = await api.discoverSchema({
        provider,
        maxTables: options?.maxTables || 50,
        includeMetadata: options?.includeMetadata || true,
        persist: true,
      });
      
      // Get the full schema data
      const schemaData = await api.getCachedSchema(provider);
      dispatch({ type: 'SET_SCHEMA', payload: { provider, schema: schemaData } });
    } catch (error: any) {
      dispatch({ type: 'SET_SCHEMA_ERROR', payload: error.message });
      throw error;
    } finally {
      dispatch({ type: 'SET_SCHEMA_LOADING', payload: false });
    }
  };

  const getCachedSchema = async (provider: 'zoho' | 'powerbi', forceRefresh?: boolean) => {
    try {
      const schemaData = await api.getCachedSchema(provider, { forceRefresh });
      dispatch({ type: 'SET_SCHEMA', payload: { provider, schema: schemaData } });
    } catch (error: any) {
      console.error('  ❌ Failed to get schema:', error.message);
      dispatch({ type: 'SET_SCHEMA_ERROR', payload: error.message });
      throw error;
    }
  };

  const clearSchemaCache = async (provider?: 'zoho' | 'powerbi') => {
    try {
      if (provider) {
        await api.clearProviderCache(provider);
        dispatch({ type: 'SET_SCHEMA', payload: { provider, schema: null } });
      } else {
        await api.clearAllSchemaCache();
        dispatch({ type: 'SET_SCHEMA', payload: { provider: 'zoho', schema: null } });
        dispatch({ type: 'SET_SCHEMA', payload: { provider: 'powerbi', schema: null } });
      }
    } catch (error: any) {
      throw error;
    }
  };

  // Conversation Management
  const startAnalyticsConversation = (provider: 'zoho' | 'powerbi'): string => {
    const conversationId = `analytics-${provider}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const conversation: AnalyticsConversation = {
      id: conversationId,
      title: `${provider === 'zoho' ? 'Zoho Analytics' : 'Power BI'} Conversation`,
      provider,
      schemaContext: [],
      messages: [],
      createdAt: new Date().toISOString(),
      lastMessageAt: new Date().toISOString(),
    };

    dispatch({ type: 'ADD_CONVERSATION', payload: conversation });
    dispatch({ type: 'SET_ACTIVE_CONVERSATION', payload: conversationId });

    return conversationId;
  };

  const sendAnalyticsMessage = async (conversationId: string, message: string, context?: any) => {
    const conversation = state.conversations.find(c => c.id === conversationId);
    if (!conversation) {
      console.error('  ❌ Conversation not found!', conversationId);
      throw new Error('Conversation not found');
    }

    const userMessage: AnalyticsMessage = {
      id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      content: message,
      sender: 'user',
      timestamp: new Date().toISOString(),
      analyticsContext: context,
    };

    dispatch({ type: 'ADD_MESSAGE', payload: { conversationId, message: userMessage } });

    try {
      const response = await api.sendAnalyticsMessageStream({
        message,
        conversationId,
        channel: 'web',
        context,
        analyticsContext: context,
        // @ts-ignore
        analyticsProvider: conversation.provider, // Pass the provider from the conversation
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let assistantContent = '';
      let analyticsData: any = null;

      const processDataLine = (dataLine: string) => {
        const data = dataLine.slice(6).trim();
        if (data === '[DONE]') return;
        try {
          const parsed = JSON.parse(data);

          // Some gateways may omit type for tasks; handle both shapes
          const isTasks = parsed.type === 'tasks' || (!!parsed.tasks && Array.isArray(parsed.tasks));

          if (parsed.type === 'content') {
            assistantContent += parsed.content ?? '';
          } else if (parsed.type === 'chunk') {
            assistantContent += parsed.content ?? '';
          } else if (isTasks) {
            const tasksArr = parsed.tasks ?? [];
            if (tasksArr.length > 0) {
              const analyticsTask = tasksArr.find((t: any) => t.id === 'analytics.result');
              if (analyticsTask && analyticsTask.params) {
                analyticsData = analyticsTask.params;
              }
            }
          }
        } catch (e) {
          console.warn('❌ Failed to parse SSE data:', e);
        }
      };

      while (reader) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            processDataLine(line);
          }
        }
      }

      // Flush any trailing buffer (in case the last event didn't end with a newline)
      if (buffer && buffer.startsWith('data: ')) {
        processDataLine(buffer);
        buffer = '';
      }

      const assistantMessage: AnalyticsMessage = {
        id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        content: assistantContent || 'No response received',
        sender: 'assistant',
        timestamp: new Date().toISOString(),
        analyticsContext: context,
        ...(analyticsData && {
          chart: analyticsData.chart,
          rows: analyticsData.rows,
          columns: analyticsData.columns,
          rowCount: analyticsData.rowCount,
          sql: analyticsData.sql,
        }),
      };

      dispatch({ type: 'ADD_MESSAGE', payload: { conversationId, message: assistantMessage } });
      dispatch({ type: 'UPDATE_CONVERSATION', payload: { 
        id: conversationId, 
        updates: { lastMessageAt: new Date().toISOString() } 
      } });
    } catch (error: any) {
      const errorMessage: AnalyticsMessage = {
        id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        content: `Error: ${error.message}`,
        sender: 'assistant',
        timestamp: new Date().toISOString(),
      };
      dispatch({ type: 'ADD_MESSAGE', payload: { conversationId, message: errorMessage } });
      throw error;
    }
  };

  const setActiveConversation = (conversationId: string | null) => {
    dispatch({ type: 'SET_ACTIVE_CONVERSATION', payload: conversationId });
  };

  // UI Actions
  const setActiveProvider = (provider: 'zoho' | 'powerbi' | null) => {
    dispatch({ type: 'SET_ACTIVE_PROVIDER', payload: provider });
  };

  const setSetupStep = (step: 'configuration' | 'oauth' | 'schema' | 'complete') => {
    dispatch({ type: 'SET_SETUP_STEP', payload: step });
  };

  const openSetupModal = () => {
    dispatch({ type: 'SET_SETUP_MODAL', payload: true });
  };

  const closeSetupModal = () => {
    dispatch({ type: 'SET_SETUP_MODAL', payload: false });
  };

  const actions: AnalyticsActions = {
    loadConfiguration,
    updateConfiguration,
    checkProviderStatus,
    connectProvider,
    refreshCredentials,
    revokeCredentials,
    discoverSchema,
    getCachedSchema,
    clearSchemaCache,
    startAnalyticsConversation,
    sendAnalyticsMessage,
    setActiveConversation,
    setActiveProvider,
    setSetupStep,
    openSetupModal,
    closeSetupModal,
  };

  return (
    <AnalyticsContext.Provider value={{ state, actions }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

// Custom Hook
export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

export default AnalyticsContext;